import {langLookup} from "../../components/editable_entries/EditableCardEntryLanguages";
import {SC_Department} from "./enums/SC_Department";

export class SC_User {
    static inputKeys = {
        id: 'id',
        firstName: 'fn',
        lastName: 'ln',
        displayName: 'dn',
        isCrewLead: 'isCrewLead',
        ownAll: 'ownAll',
        coOwn: 'coOwn',
        ownOwn: 'ownOwn',
        pcid: 'pcid',
        pn: 'pn',
        email: 'email',
        langs: 'langs',
        deactive: 'deactive',
        managers: 'managers',
        state: 'state',
        roles: 'roles',
    } as const;

    public insiteID: number | null;
    public firstName: string | null;
    public lastName: string | null;
    public displayName: string | null;
    public isCrewLead: boolean | null;
    public ownAll: boolean | null;
    public coOwn: boolean | null;
    public ownOwn: boolean | null;
    public paycomID: number | null;
    public phone: string | null;
    public email: string | null;
    public langs: string[] | null;
    public deactive: boolean | null;
    public managers: SC_User[] | null;
    public managerIDs: number[] | null;
    public state: string | null;
    public departments: SC_Department[] | null;

    constructor({
        id = null,
        firstName = null,
        lastName = null,
        displayName = null,
        isCrewLead = null,
                    ownAll = null,
        coOwn = null,
        ownOwn = null,
        pcid = null,
        pn = null,
        email = null,
        langs = null,
        deactive = null,
        managerIDs = null,
        state = null,
        departments = null,
    } = {}) {
        this.insiteID = id;
        this.firstName = firstName;
        this.lastName = lastName;
        this.displayName = displayName;
        this.isCrewLead = isCrewLead;
        this.ownAll = ownAll;
        this.coOwn = coOwn;
        this.ownOwn = ownOwn;
        this.paycomID = pcid;
        this.phone = pn;
        this.email = email;
        this.langs = null;
        this.deactive = deactive;
        this.managerIDs = managerIDs;
        this.state = state;
        this.departments = departments;

        if (langs) {
            this.langs = Object.entries(langs).filter(([, set]) => set).map(([k,]) => langLookup[k]);
        }
    }

    /**
     * @param {Object} jsonin
     * @returns {SC_User}
     */
    static fromAPI(jsonin: Record<string, unknown>): SC_User {
        return new SC_User({
            id: jsonin[this.inputKeys.id] ?? null,
            firstName: jsonin[this.inputKeys.firstName] ?? null,
            lastName: jsonin[this.inputKeys.lastName] ?? null,
            displayName: jsonin[this.inputKeys.displayName] ?? null,
            isCrewLead: jsonin[this.inputKeys.isCrewLead] ?? null,
            ownAll: jsonin[this.inputKeys.ownAll] ?? null,
            coOwn: jsonin[this.inputKeys.coOwn] ?? null,
            ownOwn: jsonin[this.inputKeys.ownOwn] ?? null,
            pcid: jsonin[this.inputKeys.pcid] ?? null,
            pn: jsonin[this.inputKeys.pn] ?? null,
            email: jsonin[this.inputKeys.email] ?? null,
            langs: jsonin[this.inputKeys.langs] ?? null,
            deactive: jsonin[this.inputKeys.deactive] ?? null,
            managerIDs: jsonin[this.inputKeys.managers] ?? null,
            state: jsonin[this.inputKeys.state] ?? null,
            departments: (jsonin[this.inputKeys.roles] as string[] | null)?.map(SC_Department.fromAPIString).filter(Boolean) ?? null,
        });
    }

    toAPI(): Record<string, unknown> {
        return {
            internalID: this.insiteID,
            firstName: this.firstName,
            lastName: this.lastName,
            displayName: this.displayName,
            isCrewLead: this.isCrewLead,
            pcid: this.paycomID,
            deactive: this.deactive,
            state: this.state,
        };
    }

    clone(): SC_User {
        const cloned = new SC_User();

        cloned.insiteID = this.insiteID;
        cloned.firstName = this.firstName;
        cloned.lastName = this.lastName;
        cloned.displayName = this.displayName;
        cloned.isCrewLead = this.isCrewLead;
        cloned.ownAll = this.ownAll;
        cloned.ownOwn = this.ownOwn;
        cloned.coOwn = this.coOwn;
        cloned.paycomID = this.paycomID;
        cloned.phone = this.phone;
        cloned.email = this.email;
        cloned.deactive = this.deactive;
        cloned.managerIDs = this.managerIDs;
        cloned.state = this.state;
        cloned.departments = this.departments;

        return cloned;
    }

    merge(other: SC_User): SC_User {
        if (other.insiteID !== null) this.insiteID = other.insiteID;
        if (other.firstName !== null) this.firstName = other.firstName;
        if (other.lastName !== null) this.lastName = other.lastName;
        if (other.displayName !== null) this.displayName = other.displayName;
        if (other.isCrewLead !== null) this.isCrewLead = other.isCrewLead;
        if (other.ownAll !== null) this.ownAll = other.ownAll;
        if (other.ownOwn !== null) this.ownOwn = other.ownOwn;
        if (other.coOwn !== null) this.coOwn = other.coOwn;
        if (other.paycomID !== null) this.paycomID = other.paycomID;
        if (other.phone !== null) this.phone = other.phone;
        if (other.email !== null) this.email = other.email;
        if (other.deactive !== null) this.deactive = other.deactive;
        if (other.managerIDs !== null) this.managerIDs = other.managerIDs;
        if (other.state !== null) this.state = other.state;
        if (other.departments !== null) this.departments = other.departments;

        return this;
    }

    static sortUsers(a: SC_User, b: SC_User): number {
        return a.name.localeCompare(b.name);
    }

    get name(): string {
        const joinedName = [this.firstName, this.lastName].filter(Boolean).join(" ");

        if (joinedName)
            return joinedName;
        else
            return this.displayName;
    }

    scavengeUsers(userCache: SC_User[]) {
        if (this.managerIDs) {
            this.managers = this.managerIDs.map(it => userCache.find(other => other.insiteID === it)).filter(Boolean)
        }
    }

    get recursiveManagers(): SC_User[] {
        let managers = {};
        let newManagers = this.managers ?? [];
        let managerment = [];

        while (newManagers.length) {
            managerment = managerment.concat(newManagers);

            newManagers.forEach(m => managers[m.insiteID] = m);

            newManagers = newManagers.map(it => it.managers ?? []).flat(1).filter(it => !(it.insiteID in managers));
        }

        return managerment;
    }
}
